/** Item-level comments — attach discussion to a specific project/task/asset/account/contract. */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChatCircleText, X, PaperPlaneRight, Paperclip, Trash } from "@phosphor-icons/react";

export const useCommentCounts = (section, pov) => {
  const [counts, setCounts] = useState({});
  const refresh = () => {
    if (!pov) return;
    api.post("/epbcs/comments/list", { section, pov })
      .then((r) => setCounts(r.data.counts || {})).catch(() => {});
  };
  useEffect(refresh, [section, JSON.stringify(pov)]); // eslint-disable-line
  return { counts, refresh };
};

export const ItemComments = ({ section, pov, itemType, itemKey, itemLabel, count = 0, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.post("/epbcs/comments/list", { section, pov })
    .then((r) => setRows((r.data.comments || []).filter((c) => c.item_key === itemKey)));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const send = async () => {
    if (!text.trim() && !file) return;
    setBusy(true);
    try {
      const attachments = [];
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/epbcs/approvals/upload", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        attachments.push(r.data);
      }
      await api.post("/epbcs/comments", { section, pov, item_type: itemType,
        item_key: itemKey, item_label: itemLabel, text, attachments });
      setText(""); setFile(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add comment");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/epbcs/comments/${id}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`item-comments-btn-${itemKey}`} title={`Comments — ${itemLabel || itemKey}`}
        className={`relative inline-flex items-center ${count ? "text-[#C8A24B]" : "text-slate-300 hover:text-slate-500"}`}>
        <ChatCircleText size={15} weight={count ? "fill" : "regular"} />
        {count > 0 && (
          <span className="absolute -top-1.5 -right-2 bg-[#0B1E3F] text-white text-[8px] font-bold rounded-full px-1 leading-[12px]"
            data-testid={`item-comments-count-${itemKey}`}>{count}</span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1E3F]/40 p-4"
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}>
          <div className="bg-white w-full max-w-md shadow-xl border border-slate-200 max-h-[80vh] flex flex-col"
            data-testid={`item-comments-dialog-${itemKey}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 bg-[#0B1E3F] text-white flex-none">
              <span className="text-[11px] font-bold uppercase tracking-wider truncate">
                Comments — {itemLabel || itemKey}
              </span>
              <button onClick={() => setOpen(false)} className="text-gray-300 hover:text-white"><X size={15} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {rows === null ? <p className="text-[12px] text-slate-400">Loading…</p>
                : rows.length === 0 ? <p className="text-[12px] text-slate-400">No comments yet — start the discussion.</p>
                : rows.map((c) => (
                  <div key={c.id} className="border border-slate-100 bg-slate-50 rounded-sm p-2.5" data-testid={`item-comment-${c.id}`}>
                    <div className="flex items-center justify-between text-[10px] text-slate-400">
                      <span><b className="text-slate-600">{c.by}</b> · {c.at?.slice(0, 16).replace("T", " ")}</span>
                      <button onClick={() => del(c.id)} className="text-slate-300 hover:text-red-600"><Trash size={12} /></button>
                    </div>
                    {c.text && <p className="mt-1 text-[12px] text-slate-700 whitespace-pre-wrap">{c.text}</p>}
                    {(c.attachments || []).map((f) => (
                      <a key={f.id} href={`${api.defaults.baseURL}/epbcs/approvals/file/${f.id}`}
                        target="_blank" rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#0B1E3F] underline">
                        <Paperclip size={11} /> {f.name}
                      </a>
                    ))}
                  </div>
                ))}
            </div>
            <div className="flex-none border-t border-slate-200 p-2.5 flex items-center gap-2">
              <label className="cursor-pointer text-slate-400 hover:text-[#0B1E3F]" title={file ? file.name : "Attach a file"}>
                <Paperclip size={16} className={file ? "text-[#C8A24B]" : ""} />
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <input value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Add a comment…" data-testid={`item-comment-input-${itemKey}`}
                className="flex-1 border border-slate-200 px-2.5 py-1.5 text-[12px] rounded-sm focus:border-[#C8A24B] outline-none" />
              <button onClick={send} disabled={busy || (!text.trim() && !file)}
                data-testid={`item-comment-send-${itemKey}`}
                className="p-2 bg-[#0B1E3F] text-white rounded-sm disabled:opacity-40">
                <PaperPlaneRight size={13} weight="fill" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
